import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { MockChatService, Chat } from '../../services-mock/mock-chat.service';

@Component({
  selector: 'app-chats',
  standalone: true,
  imports: [CommonModule, RouterModule, AvatarModule],
  template: `
    <div class="chats-container">
      <div class="chats-header">
        <h2 class="chats-title">Messages</h2>
      </div>

      <div class="chats-list" *ngIf="chats.length > 0 && !loading">
        <div
          *ngFor="let chat of chats"
          class="chat-item"
          [routerLink]="['/chats', chat.id]"
        >
          <div class="chat-avatar">
            <p-avatar
              [label]="chat.participantInitials"
              shape="circle"
              size="normal"
            ></p-avatar>
            <span class="unread-badge" *ngIf="chat.unreadCount > 0">{{
              chat.unreadCount
            }}</span>
          </div>
          <div class="chat-content">
            <div class="chat-header">
              <div class="chat-name">{{ chat.participantName }}</div>
              <div class="chat-time">{{ formatTime(chat.lastMessageTime) }}</div>
            </div>
            <div class="chat-preview">
              <span [class.unread]="chat.unreadCount > 0">{{
                chat.lastMessage
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="no-chats" *ngIf="chats.length === 0 && !loading">
        <div class="no-chats-content">
          <i class="pi pi-comments no-chats-icon"></i>
          <h3>No messages yet</h3>
          <p>Start a conversation to see your messages here.</p>
        </div>
      </div>

      <div class="loading" *ngIf="loading">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Loading messages...</span>
      </div>
    </div>
  `,
  styles: [
    `
      .chats-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1.5rem;
        background-color: #f5f5f5;
        min-height: calc(100vh - 60px);
        box-sizing: border-box;
        overflow-x: hidden;
        width: 100%;
      }

      .chats-header {
        margin-bottom: 1.5rem;
      }

      .chats-title {
        font-size: 1.75rem;
        font-weight: 600;
        color: #000000;
        margin: 0;
      }

      .chats-list {
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        box-sizing: border-box;
        width: 100%;
      }

      .chat-item {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e5e5e5;
        cursor: pointer;
        transition: background-color 0.2s ease;
        text-decoration: none;
        color: inherit;
        box-sizing: border-box;
        width: 100%;
        overflow: hidden;
      }

      .chat-item:last-child {
        border-bottom: none;
      }

      .chat-item:hover {
        background-color: #f9f9f9;
      }

      .chat-avatar {
        position: relative;
        flex-shrink: 0;
      }

      .unread-badge {
        position: absolute;
        top: -4px;
        right: -4px;
        background-color: #0066cc;
        color: #ffffff;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        font-weight: 600;
        border: 2px solid #ffffff;
      }

      .chat-content {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        box-sizing: border-box;
      }

      .chat-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.25rem;
        gap: 0.5rem;
        min-width: 0;
      }

      .chat-name {
        font-weight: 600;
        font-size: 0.95rem;
        color: #000000;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .chat-time {
        font-size: 0.85rem;
        color: #999999;
        flex-shrink: 0;
        white-space: nowrap;
      }

      .chat-preview {
        font-size: 0.9rem;
        color: #666666;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
        box-sizing: border-box;
      }

      .chat-preview span {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .chat-preview .unread {
        font-weight: 600;
        color: #000000;
      }

      .no-chats {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        padding: 3rem;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .no-chats-content {
        text-align: center;
        max-width: 400px;
      }

      .no-chats-icon {
        font-size: 4rem;
        color: #cccccc;
        margin-bottom: 1rem;
      }

      .no-chats-content h3 {
        font-size: 1.5rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 0.5rem 0;
      }

      .no-chats-content p {
        font-size: 1rem;
        color: #666666;
        margin: 0;
      }

      .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        padding: 3rem;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        color: #666666;
      }

      .loading i {
        font-size: 2rem;
      }

      /* Mobile responsive styles */
      @media (max-width: 767px) {
        .chats-container {
          padding: 0.75rem;
          padding-bottom: 4rem; /* Account for bottom navigation */
          min-height: calc(100vh - 50px - 60px); /* Header + bottom nav */
        }

        .chats-header {
          margin-bottom: 1rem;
        }

        .chats-title {
          font-size: 1.5rem;
        }

        .chat-item {
          padding: 0.75rem 1rem;
          gap: 0.75rem;
        }

        .chat-name {
          font-size: 0.9rem;
        }

        .chat-time {
          font-size: 0.8rem;
        }

        .chat-preview {
          font-size: 0.85rem;
        }
      }
    `,
  ],
})
export class ChatsComponent implements OnInit {
  chats: Chat[] = [];
  loading = true;

  constructor(private chatService: MockChatService) {}

  ngOnInit() {
    this.loadChats();
  }

  loadChats() {
    this.loading = true;
    this.chatService.getChats().subscribe((chats) => {
      this.chats = chats;
      this.loading = false;
    });
  }

  formatTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  }
}

